import {
  collection,
  doc,
  documentId,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';

function toDate(v: unknown): Date | null {
  if (!v) return null;
  const t = v as { toDate?: () => Date; seconds?: number };
  if (typeof t?.toDate === 'function') return t.toDate();
  if (typeof t?.seconds === 'number') return new Date(t.seconds * 1000);
  return null;
}

// --------------------------------------------------------------------
// Client
// --------------------------------------------------------------------
export interface ClientDoc {
  id: string;
  name: string;
  slug: string;
}
export function listenClient(
  clientId: string,
  cb: (c: ClientDoc | null) => void,
): Unsubscribe {
  return onSnapshot(doc(db, `clients/${clientId}`), (snap) => {
    if (!snap.exists()) return cb(null);
    const x = snap.data();
    cb({ id: snap.id, name: x.name ?? '', slug: x.slug ?? snap.id });
  });
}

// --------------------------------------------------------------------
// Staff client picker
// --------------------------------------------------------------------
export interface ClientListItem {
  id: string;
  name: string;
}

/** Firestore caps `in` at 30 values per query. */
const IN_CHUNK = 30;

/**
 * Clients a staff user is allowed to preview, for the portal-root picker.
 *
 * Admins can list `clients` unconstrained. Project coordinators cannot:
 * rules scope them to `serp_assignedClientIds`, and Firestore rejects any
 * list query it can't statically prove is permitted — so their query has
 * to name the ids explicitly, in chunks of 30.
 */
export async function listClientsForStaff(
  role: 'admin' | 'project_coordinator',
  assignedClientIds: string[],
): Promise<ClientListItem[]> {
  const byId = new Map<string, ClientListItem>();

  const collect = (docs: { id: string; data: () => Record<string, unknown> }[]) => {
    for (const d of docs) {
      byId.set(d.id, { id: d.id, name: (d.data().name as string) || d.id });
    }
  };

  if (role === 'admin') {
    collect((await getDocs(collection(db, 'clients'))).docs);
  } else {
    for (let i = 0; i < assignedClientIds.length; i += IN_CHUNK) {
      const chunk = assignedClientIds.slice(i, i + IN_CHUNK);
      if (chunk.length === 0) continue;
      const snap = await getDocs(
        query(collection(db, 'clients'), where(documentId(), 'in', chunk)),
      );
      collect(snap.docs);
    }
  }

  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

// --------------------------------------------------------------------
// Scorecard
// --------------------------------------------------------------------
export interface ScorecardDoc {
  asOf: Date | null;
  healthScore: number;
  scoreBand: 'critical' | 'at-risk' | 'on-track' | 'strong';
  trend30Day: number;
  trend90DaySparkline: number[];
  // Phase 13a — real per-week page-1 negative / positive history. Optional
  // for backward compat with scorecards written before this field shipped.
  negativesPage1Sparkline?: number[];
  positivesPage1Sparkline?: number[];
  negativesOnPage1: number;
  positivesOnPage1: number;
  previousPeriod: {
    negativesOnPage1: number;
    positivesOnPage1: number;
    healthScore: number;
  };
  lastPullAt: Date | null;
}

export function listenScorecard(
  clientId: string,
  cb: (s: ScorecardDoc | null) => void,
): Unsubscribe {
  return onSnapshot(doc(db, `clients/${clientId}/scorecard/current`), (snap) => {
    if (!snap.exists()) return cb(null);
    const x = snap.data();
    cb({
      asOf: toDate(x.asOf),
      healthScore: x.healthScore ?? 0,
      scoreBand: x.scoreBand ?? 'at-risk',
      trend30Day: x.trend30Day ?? 0,
      trend90DaySparkline: Array.isArray(x.trend90DaySparkline) ? x.trend90DaySparkline : [],
      negativesPage1Sparkline: Array.isArray(x.negativesPage1Sparkline)
        ? x.negativesPage1Sparkline
        : undefined,
      positivesPage1Sparkline: Array.isArray(x.positivesPage1Sparkline)
        ? x.positivesPage1Sparkline
        : undefined,
      negativesOnPage1: x.negativesOnPage1 ?? 0,
      positivesOnPage1: x.positivesOnPage1 ?? 0,
      previousPeriod: {
        negativesOnPage1: x.previousPeriod?.negativesOnPage1 ?? 0,
        positivesOnPage1: x.previousPeriod?.positivesOnPage1 ?? 0,
        healthScore: x.previousPeriod?.healthScore ?? x.healthScore ?? 0,
      },
      lastPullAt: toDate(x.lastPullAt),
    });
  });
}

// --------------------------------------------------------------------
// Reports (published only — rules block drafts for clients)
// --------------------------------------------------------------------
export interface PublishedReportListItem {
  id: string;
  title: string;
  uniqueSlug: string;
  periodStart: Date | null;
  periodEnd: Date | null;
  publishedAt: Date | null;
  /** First sentence of the report's narrative.overview, used for the
   *  dashboard hero subtitle (Phase 13a Tier 1B) and the inline excerpt
   *  on the latest report (Tier 2B). Empty string when the report has
   *  no overview narrative. */
  narrativeOverviewExcerpt: string;
}
export function listenPublishedReports(
  clientId: string,
  cb: (r: PublishedReportListItem[]) => void,
): Unsubscribe {
  // IMPORTANT: orderBy must match the deployed composite index in
  // firestore.indexes.json — `status ASC + publishedAt DESC`. Earlier
  // versions used `periodEnd` here which silently failed with a
  // missing-index error (Firestore returns empty rather than throwing
  // visibly), and clients saw "0 published" on the dashboard while
  // reports were live in Firestore. Sorting by publishedAt gives the
  // same effective order for monthly reports — they're typically
  // published within a day or two of period end — and `publishedAt`
  // also matches the user-facing "Published Apr 28" label, so the
  // ordering reads naturally.
  const q = query(
    collection(db, `clients/${clientId}/reports`),
    where('status', '==', 'published'),
    orderBy('publishedAt', 'desc'),
  );
  return onSnapshot(q, (snap) => {
    cb(
      snap.docs.map((d) => {
        const x = d.data();
        const overview =
          (x.narrative as { overview?: string } | undefined)?.overview ?? '';
        return {
          id: d.id,
          title: x.title ?? '',
          uniqueSlug: x.uniqueSlug ?? '',
          periodStart: toDate(x.periodStart),
          periodEnd: toDate(x.periodEnd),
          publishedAt: toDate(x.publishedAt),
          narrativeOverviewExcerpt: extractFirstSentence(overview),
        };
      }),
    );
  });
}

/**
 * Pull the first sentence (or first ~180 chars) out of a markdown
 * paragraph. Strips trailing punctuation and any leading markdown
 * heading marks. Used for the hero subtitle + the inline report
 * excerpt — both want a short, plain-text gloss of the report's
 * "what changed this period" lead.
 */
function extractFirstSentence(markdown: string): string {
  const cleaned = markdown
    .replace(/^#+\s*/gm, '') // strip markdown headers
    .replace(/[*_`]/g, '') // strip emphasis markers
    .trim();
  if (!cleaned) return '';
  // Match up to the first sentence terminator. Allow `.`, `!`, `?` —
  // require at least one character before so abbreviations like "Dr."
  // at the start don't yield a 0-char sentence.
  const match = cleaned.match(/^[^.!?]+[.!?]/);
  const first = match ? match[0].trim() : cleaned;
  if (first.length <= 220) return first;
  // Fall back to a hard truncation with ellipsis when there's no
  // obvious sentence break in the first 220 chars.
  return first.slice(0, 200).trim() + '…';
}

// Individual report (for the detail view). Return raw data — component
// knows how to render narrative + snapshot.
export function listenReportBySlug(
  clientId: string,
  slug: string,
  cb: (r: Record<string, unknown> | null) => void,
): Unsubscribe {
  const q = query(
    collection(db, `clients/${clientId}/reports`),
    where('uniqueSlug', '==', slug),
    where('status', '==', 'published'),
  );
  return onSnapshot(q, (snap) => {
    if (snap.empty) return cb(null);
    const d = snap.docs[0];
    cb({ id: d.id, ...d.data() });
  });
}

/**
 * A single report by document id, with no `status` filter — the staff draft
 * preview, since drafts have no `uniqueSlug` (publishReport allocates it).
 *
 * Leaving the status unfiltered is safe here because this is a document read,
 * not a list query: rules evaluate it per-document, and only staff may read a
 * draft. A client user who guessed a draft id gets permission-denied, which
 * the error callback turns into the same "not found" the page already shows.
 */
export function listenReportById(
  clientId: string,
  reportId: string,
  cb: (r: Record<string, unknown> | null) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, `clients/${clientId}/reports/${reportId}`),
    (snap) => cb(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    () => cb(null),
  );
}

// "What we did this period" — from the latest published report's actions.
export function listenLatestActions(
  clientId: string,
  cb: (actions: string[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, `clients/${clientId}/reports`),
    where('status', '==', 'published'),
    orderBy('publishedAt', 'desc'),
  );
  return onSnapshot(q, (snap) => {
    if (snap.empty) return cb([]);
    const d = snap.docs[0].data();
    const actions = Array.isArray(d.actions) ? d.actions : [];
    if (actions.length > 0) return cb(actions as string[]);
    // Fallback: parse the PM-authored "actionsThisPeriod" markdown bullet list.
    const md = (d.narrative?.actionsThisPeriod as string | undefined) ?? '';
    const bullets = md
      .split('\n')
      .map((l) => l.replace(/^[-*]\s*/, '').trim())
      .filter(Boolean);
    cb(bullets);
  });
}
